<template>
  <div ref="catalog-ref" class="catalog">
    <slot name="title" :catalog-title="catalogTitle" />

    <slot name="category" />

    <!-- за запитом нічого не знайдено, лише на сторінці пошуку -->
    <slot name="notfound" />

    <div v-show="!$slots.notfound">
      <div ref="catalog-bar-ref" class="catalog-bar--controller z-index__10">
        <div
          class="catalog-bar-wrapper"
          :class="{
            'catalog-bar-wrapper--shadow': catalogBarBoxShadow
          }"
        >
          <Container class="catalog-bar-container">
            <div class="catalog-bar">
              <div class="catalog-bar__selected show-from-desktop">
                <!--  кількість обраних товарів  -->
                <span class="catalog-bar__selected-title">
                  {{ $t('catalog.selected') }}
                  <span
                    class="catalog-bar__selected-value"
                    :style="{ opacity: fetching ? ': .5' : '' }"
                  >
                    {{ countFormated }}
                  </span>
                </span>

                <!--  список обраних фільтрів  -->
                <CatalogSelectedFilters
                  v-if="hasSelectedFilters"
                  :selected-filter-query="selectedFilter"
                  @handle-clear="handleClearFilters"
                  @delete-filter="handleRemoveFilterQuery"
                />
              </div>

              <div class="catalog-bar__settings">
                <!--  кнопка відкриття модалки категорій  -->
                <div
                  v-if="showCategoryFilter"
                  class="catalog-bar__category show-until-desktop"
                  @click="showCategoryMobile = true"
                >
                  <span class="catalog-bar__icon icon-menu" />
                  {{ $t('catalog.filters.category') }}
                </div>

                <!--  кнопка відкриття модалки фільтрів  -->
                <div
                  class="catalog-bar__filter show-until-desktop"
                  :disabled="filters.length === 0"
                  @click="showFilterMobile = true"
                >
                  <span
                    v-if="!selectedFilter.length"
                    class="catalog-bar__icon icon-filter"
                  />
                  <span v-else class="catalog-bar__icon-count">{{
                    selectedFilter.length
                  }}</span>
                  {{ $t('catalog.filters.title_mobile') }}
                </div>

                <!--  фільтр сортування  -->
                <div
                  v-if="!hideSorting"
                  class="catalog-bar__sorting"
                  :class="{
                    'catalog-bar__sorting--category': !showCategoryFilter
                  }"

                >
                  <BaseSelect
                    v-intersect
                    :selected="orderingModel"
                    :placeholder="sortingOptions[0].label"
                    width="inherit"
                    :options="sortingOptions"
                    @update:model-value="handleChangeSort"
                    @intersect:enter="onShowSort"
                  >
                    <template
                      #mobile-trigger="{ placeholderString, showMobileSelect }"
                    >
                      <div
                        class="catalog-bar__sorting-trigger"
                        @click="showMobileSelect"
                      >
                        <span class="icon-sort" />
                        <span v-show="!showCategoryFilter">{{
                          placeholderString
                        }}</span>
                      </div>
                    </template>
                  </BaseSelect>
                </div>
              </div>
            </div>
          </Container>
        </div>
      </div>
      <div ref="products-ref" class="products">
        <div>
          <template v-if="showCategoryFilter">
            <CatalogCategoryFilter
              :is-opened="showCategoryMobile"
              :title="categoryFilterTitle"
              @close-modal="showCategoryMobile = false"
              @change-category="scrollToCatalogSmooth"
            />
          </template>

          <CatalogProductsFilters
            :show-modal-mobile="showFilterMobile"
            :selected-filter-count="selectedFilter.length"
            :show-bonuses-filter="!bonusesSorting"
            :show-in-stock-filter="showInStockFilter"
            :hide-condition="!isShownCondition"
            @change="showFilterMobile = $event"
          >
            <template v-if="showFilterMobile && hasSelectedFilters">
              <CatalogSelectedFilters
                :show-clear-button="hasSelectedFilters"
                :selected-filter-query="selectedFilter"
                @handle-clear="handleClearFilters"
                @delete-filter="handleRemoveFilterQuery"
              />
            </template>
          </CatalogProductsFilters>
        </div>

        <div class="products__container">
          <slot
            name="list"
            :go-to-offer="hasSelectedOfferFilters"
          />

          <CommonPagination
            :fetching="fetching"
            :total-items="productsCount"
            show-more
            style="margin-top: 2rem"
            @on-change-page="handleChangePage"
            @on-show-more="changeMultiplePage"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { OFFER_FILTERS, PRICE_FILTER_CODE, CITY_FILTER_CODE } from '@/constants/common'
import { getSellerSortOptions, getBonusesSortOptions, getBaseSortedOptions } from '@/constants/options'
import type { SelectedFilter, SortingValue } from '~/types/catalog'
import type { PageOptions } from '~/types/page'

type Props = {
  title?: string
  categoryFilterTitle?: string
  hideSorting?: boolean
  isSearchPage?: boolean
  showCategoryFilter?: boolean
  showInStockFilter?: boolean
  bonusesSorting?: boolean
  sellerSorting?: boolean
  sortingNewFirst?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  title: '',
  categoryFilterTitle: '',
  showInStockFilter: false
})
const route = useRoute()
const i18n = useI18n()
const { $device } = useNuxtApp()
const commonMetricsStore = useCommonMetricsStore()
const catalogFiltersStore = useCatalogFiltersStore()
const catalogCategoriesStore = useCatalogCategoriesStore()
const catalogCategoryStore = useCatalogCategoryStore()
const catalogProductsStore = useCatalogProductsStore()

const catalogContainerRef = useTemplateRef<HTMLDivElement>('catalog-ref')
const productsEl = useTemplateRef<HTMLElement>('products-ref')
const catalogBarEl = useTemplateRef<HTMLDivElement>('catalog-bar-ref')

const showCategoryMobile = shallowRef(false)
const showFilterMobile = shallowRef(false)
const catalogBarBoxShadow = shallowRef(false)

const {
  selectedCategories
} = storeToRefs(catalogCategoriesStore)

const {
  isShownCondition
} = storeToRefs(catalogCategoryStore)

const {
  fetching,
  count: productsCount,
} = storeToRefs(catalogProductsStore)

const {
  ordering,
  filterCity,
  filterQuery,
  list: filters
} = storeToRefs(catalogFiltersStore)

const cityName = computed(() => {
  return filterCity.value
    ? filterCity.value.public_name.split('-')[0]
    : ''
})

const catalogTitle = computed(() => {
  let title = props.title
  const { page } = route.query
  const isPage = page && page !== '1'

  if (props.isSearchPage) {
    return title
  }

  if (props.showCategoryFilter) {
    const categoryName: string = selectedCategories.value?.name || ''

    if (categoryName) {
      title = title.length === 0
        ? categoryName
        : `${title} ${i18n.t('catalog.in_category')} ${categoryName}`
    }
  }

  if (cityName.value) {
    return `${title} ${i18n.t('catalog.filters.in')} ${cityName.value} ${isPage ? `— ${i18n.t('common.is_page', { page })}` : ''}`
  }

  return `${title} ${isPage ? `— ${i18n.t('common.is_page', { page })}` : ''}`
})

const orderingModel = computed(() => {
  if (ordering.value) {
    return ordering.value
  } else if (props.bonusesSorting) {
    return 'o_recommended'
  } else if (props.sellerSorting) {
    return 'created_at'
  } else {
    return 'rating'
  }
})

const sortingOptions = computed<SortingValue[]>(() => {
  let options: SortingValue[] = []

  if (props.sortingNewFirst) {
    options = [{
      label: i18n.t('catalog.sorting_new_first'),
      value: 'created_at'
    }]
  } else if (props.bonusesSorting) {
    options = getBonusesSortOptions(i18n.t)
  } else if (props.sellerSorting) {
    options = getSellerSortOptions(i18n.t)
  }
  return options.concat(getBaseSortedOptions(i18n.t))
})

const selectedFilter = computed<SelectedFilter[]>(() => {
  const activeFilters: SelectedFilter[] = []
  const addActiveFilter = (payload: SelectedFilter) => {
    activeFilters.push(payload)
  }

  filters.value.forEach((filter) => {
    const selected = filterQuery.value[filter.code]

    if (filter.code === CITY_FILTER_CODE && selected && filterCity.value) {
      addActiveFilter({
        filterName: filter.title,
        code: filter.code,
        value: selected,
        label: cityName.value
      })
    } else {
      filter.items.forEach(({ items, type, unit = '', name, code }) => {
        const selectedValue = filterQuery.value[code === PRICE_FILTER_CODE ? 'price' : code] as string | undefined

        if (!selectedValue) {
          return
        }

        if (['slider', 'range_input'].includes(type)) {
          const [from, to] = selectedValue.split('-')
          const lowerUnit = unit.toLowerCase()

          addActiveFilter({
            filterName: filter.title,
            code,
            value: `${from}-${to}`,
            label: `${i18n.t(
              'catalog.filters.price_from'
            )} ${getFormattedPrice(+from)} ${lowerUnit} ${i18n.t(
              'catalog.filters.price_to'
            )} ${getFormattedPrice(+to)} ${lowerUnit}`
          })
        } else if (type === 'bool') {
          addActiveFilter({
            filterName: filter.title,
            code,
            value: selectedValue,
            label: name,
          })
        } else {
          selectedValue.split(',').forEach((selectedItem) => {
            for (const { value, label } of items) {
              if (value === selectedItem) {
                addActiveFilter({
                  filterName: filter.title,
                  code,
                  value,
                  label
                })
                break
              }
            }
          })
        }
      })
    }
  })

  return activeFilters
})

const hasSelectedFilters = computed(() => {
  return selectedFilter.value.length > 0
})

const hasSelectedOfferFilters = computed(() => {
  return OFFER_FILTERS.some((code) => Boolean(filterQuery.value[code]))
})

const countFormated = computed(() => {
  return getFormatedData(productsCount.value)
})

const getFormatedData = (value: number) => {
  const valueString: string = value.toString()

  if (value < 1000) {
    return valueString
  }
  return value < 10000
    ? `${valueString[0]} ${valueString.slice(1)}`
    : i18n.t('catalog.more_count', { count: 10 })
}

const scrollToCatalog = (arg?: ScrollIntoViewOptions) => {
  // The window is scrolled below the beginning of the container
  const positionCatalog = catalogContainerRef.value!.getBoundingClientRect().top

  if (positionCatalog > 0) {
    return
  }

  if (window.innerWidth > 992) {
    catalogContainerRef.value!.scrollIntoView(arg)
  } else {
    window.scrollTo(0, productsEl.value!.offsetTop - 120)
  }
}

const changeMultiplePage = (params: PageOptions) => {
  catalogFiltersStore.changeMultiplePage(params)
}

const handleChangePage = ({ page }: PageOptions) => {
  catalogFiltersStore.changePage(page)
  scrollToCatalog()
}

const scrollToCatalogSmooth = () => {
  nextTick(() => {
    scrollToCatalog({
      behavior: 'smooth'
    })
  })
}

const handleClearFilters = () => {
  catalogFiltersStore.resetFilter()

  commonMetricsStore.addMetricEvent({
    element: 'ms[catalog-filters-clear]',
    event_type: 'click',
    event_content: ''
  })
}

const handleChangeSort = async (value: string | number | null) => {
  catalogFiltersStore.changeSort(value)

  commonMetricsStore.addMetricEvent({
    element: 'ms[sort-select]',
    event_type: 'click',
    event_content: value
  })
}

const onShowSort = () => {
  commonMetricsStore.addMetricEvent({
    element: 'ms[sort-select]',
    event_type: 'view',
    event_content: ''
  })
}

const handleRemoveFilterQuery = (value: SelectedFilter) => {
  catalogFiltersStore.removeFilterQuery(value)

  commonMetricsStore.addMetricEvent({
    element: 'ms[catalog-filters-remove]',
    event_type: 'click',
    event_content: JSON.stringify(value)
  })
}

const funcResize = () => {
  if (window.innerWidth >= 992) {
    showCategoryMobile.value = false
    showFilterMobile.value = false
  }
}

const handleScroll = () => {
  if ($device.isDesktop) {
    return
  }
  const top = catalogBarEl.value!.getBoundingClientRect().top
  const showBoxShadow = top <= 1

  if (catalogBarBoxShadow.value !== showBoxShadow) {
    catalogBarBoxShadow.value = showBoxShadow
  }
}

useResizeTimeout(funcResize)
useScrollDocument(handleScroll, {
  passive: true
})

const handleScrollTop = (show: boolean) => {
  if (!show) {
    window.scrollTo(0, 0)
    nextTick(scrollToCatalog)
  }
}

watch(showFilterMobile, handleScrollTop)
watch(showCategoryMobile, handleScrollTop)
</script>

<style lang="scss" scoped>
.not-found-recommendations-list {
  margin-bottom: 4rem;

  @media only screen and (min-width: 1024px) {
    margin-bottom: 6rem;
  }
}

.catalog {
  width: 100%;
  padding-top: 15px;
  margin-top: -15px;
  @media only screen and (max-width: 1024px) {
    padding-top: 20px;
  }
}

.products {
  display: flex;

  &__name {
    font-size: 4rem;
    font-weight: 500;
    margin-bottom: 0.1rem;

    sup {
      color: $dark-gray-color;
      font-size: 1.6rem;
      font-weight: 400;
    }
  }
  &__container {
    display: flex;
    flex-direction: column;
    width: calc(100% - 350px);

    @media only screen and (max-width: 1024px) {
      width: 100%;
    }
  }
}

.catalog-bar {
  width: 100%;
  padding: 0.6rem 0;

  @media only screen and (min-width: 1024px) {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.6rem 0;
  }

  &--controller {
    min-height: 4.8rem;
    margin-bottom: 1rem;
    @media only screen and (max-width: 1024px) {
      position: sticky;
      top: 0;
    }
    @media only screen and (min-width: 1024px) {
      min-height: 5.4rem;
    }
  }

  &-wrapper {
    background-color: $white-color;
    position: absolute;
    left: -1.6rem;
    width: 100vw;

    &--shadow {
      box-shadow: 0 2px 2px rgba(0, 0, 0, 0.06);
    }

    @media only screen and (min-width: 740px) {
      left: -3rem;
    }
    @media only screen and (min-width: 1024px) {
      position: relative !important;
      box-shadow: initial !important;
      left: 0;
      width: 100%;
      .catalog-bar-container {
        width: 100%;
        padding: 0;
      }
    }

    @media only screen and (max-width: 1024px) {
      will-change: auto;
      transition: top 200ms;
    }
  }

  &__selected {
    width: 100%;
    padding-right: 1.6rem;
    margin-bottom: -0.8rem;
  }

  &__selected-title {
    display: inline-block;
    white-space: nowrap;
    margin: 0 2.4rem 0.8rem 0;
    line-height: 2.4rem;
    color: $secondary-font-color;

    @media only screen and (max-width: 1024px) {
      font-size: 1.8rem;
      color: $font-color;
      font-weight: 700;
      margin-bottom: 20px;
    }
  }

  &__selected-value {
    color: $font-color;
  }

  &__settings {
    display: flex;
    align-self: flex-start;
    @media only screen and (max-width: 1024px) {
      width: 100%;
    }
  }

  &__category,
  &__filter {
    width: 50%;
  }

  &__category,
  &__filter,
  &__sorting-trigger {
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid #e5e5e5;
    box-sizing: border-box;
    border-radius: 4px;
    padding: 0.6rem 1rem;
    cursor: pointer;
    font-size: 1.4rem;
    line-height: 1.8rem;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;

    [class^='icon-'],
    [class*=' icon-'] {
      font-size: 1.4rem;
    }

    &[disabled='disabled'] {
      pointer-events: none;
      border-color: $border-color;
      cursor: not-allowed !important;
      color: #a9a9a9;
    }
  }

  &__icon {
    margin-right: 0.4rem;
  }

  &__icon-count {
    font-size: 1.2rem;
    margin-right: 0.4rem;
    min-width: 2rem;
    padding: 0 0.4rem;
    height: 2rem;
    background: #e5e5e5;
    border-radius: 4px;
    text-align: center;
    line-height: 2rem;
  }

  &__category {
    margin-right: 0.6rem;
  }

  &__view,
  &__sorting {
    margin-left: 0.6rem;
  }

  &__sorting {
    @media only screen and (max-width: 1024px) {
      &--category {
        width: 50%;

        .icon-sort {
          margin-right: 0.4rem;
        }
      }

      .catalog-bar__sorting-trigger {
        width: initial;
      }
    }

    @media only screen and (min-width: 1024px) {
      margin-left: auto;
      align-self: flex-start;
      width: 22rem;
      flex-shrink: 0;
    }
  }


  &__sorting-trigger {
    width: 3.6rem;
    height: 3.6rem;
  }
}
</style>
